import { useMemo } from 'react';
import { CalendarDays } from 'lucide-react';

// Date helpers — operate in local time. America/Chicago will be the
// natural display because we're running in the user's browser.

function pad(n) { return String(n).padStart(2, '0'); }
function iso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

export function defaultDashboardRange() {
  // Per the brief: default to April 2026 because current month (May 2026)
  // has no data. Bookkeeper opens the dashboard and immediately sees the
  // last completed close.
  const today = new Date();
  // If today is after the 5th of a month, default to LAST FULL MONTH; if
  // before the 5th and there's data for two months ago we'd want that
  // instead. Keep it simple: default to last full calendar month.
  const lastFullMonthStart = startOfMonth(addMonths(today, -1));
  const lastFullMonthEnd = endOfMonth(lastFullMonthStart);
  return { start: iso(lastFullMonthStart), end: iso(lastFullMonthEnd) };
}

function makePreset(label, computeRange) {
  return { label, compute: computeRange };
}

const PRESETS = [
  makePreset('Last month', () => {
    const today = new Date();
    const s = startOfMonth(addMonths(today, -1));
    const e = endOfMonth(s);
    return { start: iso(s), end: iso(e) };
  }),
  makePreset('Last week', () => {
    const today = new Date();
    // Last full week, Mon-Sun ending on most recent Sunday.
    // Simple: rolling 7 days ending yesterday.
    const end = addDays(today, -1);
    const start = addDays(end, -6);
    return { start: iso(start), end: iso(end) };
  }),
  makePreset('MTD', () => {
    const today = new Date();
    const s = startOfMonth(today);
    return { start: iso(s), end: iso(today) };
  }),
];

export default function PeriodPicker({ start, end, onChange }) {
  const activePreset = useMemo(() => {
    return PRESETS.find((p) => {
      const r = p.compute();
      return r.start === start && r.end === end;
    });
  }, [start, end]);

  const applyPreset = (preset) => {
    const r = preset.compute();
    onChange(r.start, r.end);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[1px] font-bold text-[var(--color-warm-500)]">
        <CalendarDays size={13} />
        Period
      </div>

      {/* Quick-select pills */}
      <div className="inline-flex items-center bg-[var(--color-cream-100)] rounded-[var(--radius-full)] p-1 gap-0.5 border border-[var(--color-warm-100)]">
        {PRESETS.map((p) => {
          const active = activePreset?.label === p.label;
          return (
            <button
              key={p.label}
              type="button"
              onClick={() => applyPreset(p)}
              className={`
                px-3 h-7 text-[12px] font-semibold rounded-[var(--radius-full)]
                transition-colors
                ${active
                  ? 'bg-[var(--color-warm-900)] text-[var(--color-cream-50)] shadow-[var(--shadow-xs)]'
                  : 'text-[var(--color-warm-600)] hover:text-[var(--color-warm-900)]'}
              `}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Custom date range */}
      <div className="inline-flex items-center gap-2 bg-[var(--color-cream-50)] border border-[var(--color-warm-200)] rounded-[var(--radius-md)] px-3 py-1.5">
        <input
          type="date"
          value={start}
          onChange={(e) => onChange(e.target.value, end)}
          className="bg-transparent text-[13px] text-[var(--color-warm-900)] tabular-nums focus:outline-none"
        />
        <span className="text-[var(--color-warm-400)]">→</span>
        <input
          type="date"
          value={end}
          onChange={(e) => onChange(start, e.target.value)}
          className="bg-transparent text-[13px] text-[var(--color-warm-900)] tabular-nums focus:outline-none"
        />
      </div>
    </div>
  );
}
